import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  Plus, ChevronUp, ChevronDown, Pencil, Trash2, FileText,
  ShoppingCart, BookOpen, RotateCcw, Calendar, ExternalLink,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSession } from '../../contexts/SessionContext';

// Local status config — mirrors what SessionDetail used.
const LEAD_STATUS_CONFIG = {
  open:   { label: 'Open',   color: 'bg-blue-100 text-blue-700 border-blue-200' },
  won:    { label: 'Won',    color: 'bg-green-100 text-green-700 border-green-200' },
  lost:   { label: 'Lost',   color: 'bg-red-100 text-red-700 border-red-200' },
  parked: { label: 'Parked', color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

const LeadsTab = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const {
    session, leads, canManageLeads,
    showLeads, setShowLeads,
    setAddLeadOpen,
    linkedEstimateDetails,
    leadRepairEstimates,
    handleConfirmRepairEstimate,
    openEditLead, setDeleteLeadTarget,
    fmt, fmtCurrency,
    lossReasons, parkedReasons, wonReasons,
    openEstimateDetail, handleConfirmEstimate,
    sessionExchangeEstimates, setViewExchangeEst,
    openNotebook, handleCreateEstimateForLead, openOldGoldDialog,
    openStatusDialog, handleReopenLead,
    setRescheduleTarget, setRescheduleDate,
    handleConvertToOrder,
  } = useSession();

  const isAdminOrOwner = currentUser?.role === 'admin' || currentUser?.role === 'owner';

  return (
    <div className="flex-1 overflow-y-auto bg-white">
      <div className="flex-shrink-0 bg-white border-b">
        <button
          onClick={() => setShowLeads(!showLeads)}
          className="w-full px-4 py-2 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-50"
          data-testid="toggle-leads"
        >
          <span>Leads ({leads.length})</span>
          <div className="flex items-center gap-2">
            {canManageLeads && session.status === 'open' && (
              <span
                onClick={e => { e.stopPropagation(); setAddLeadOpen(true); }}
                className="text-emerald-600 hover:text-emerald-700 flex items-center text-xs font-semibold"
              >
                <Plus className="w-3 h-3 mr-0.5" /> Add Lead
              </span>
            )}
            {showLeads ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </button>
        {showLeads && (
          <div className="px-4 pb-3 space-y-2 max-h-[40vh] overflow-y-auto" data-testid="leads-panel">
            {leads.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-3">No leads yet. Add a product lead to start.</p>
            ) : leads.map(lead => {
              const sc = LEAD_STATUS_CONFIG[lead.status] || LEAD_STATUS_CONFIG.open;
              const isRepairLead = (lead.main_group || '') === 'Repair';
              const linkedEsts = linkedEstimateDetails[lead._id] || [];
              const repairEsts = (leadRepairEstimates && leadRepairEstimates[lead._id]) || [];
              const estimateCount = isRepairLead ? repairEsts.length : linkedEsts.length;
              const hasConfirmedEst = isRepairLead
                ? repairEsts.some(e => e.status === 'active')
                : linkedEsts.some(e => e.status === 'confirmed');
              const exEsts = sessionExchangeEstimates.filter(e => e.lead_id === lead._id);
              const reasonCategory = (() => {
                if (!lead.reason) return null;
                const allReasons = [...lossReasons, ...parkedReasons, ...wonReasons];
                return allReasons.find(g => g.reasons?.some(r => r.reason === lead.reason))?.category;
              })();
              return (
                <div
                  key={lead._id}
                  className={`border rounded-lg p-3 relative ${lead.status === 'open' ? 'border-blue-200' : 'border-gray-200'}`}
                  data-testid={`lead-${lead._id}`}
                >
                  {isAdminOrOwner && (
                    <div className="absolute top-2 right-2 flex gap-1">
                      {currentUser?.role === 'admin' && (
                        <button
                          onClick={e => { e.stopPropagation(); openEditLead(lead); }}
                          className="p-1.5 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Edit lead"
                          data-testid={`edit-lead-btn-${lead._id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={e => { e.stopPropagation(); setDeleteLeadTarget(lead); }}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete lead"
                        data-testid={`delete-lead-btn-${lead._id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="pr-8">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-gray-400">#{lead.lead_number || '?'}</span>
                        <span className="font-semibold text-sm text-gray-900">{lead.product_name}</span>
                        {lead.main_group && <span className="text-xs text-gray-500">{lead.main_group}</span>}
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${sc.color}`}>{sc.label}</span>
                        {estimateCount > 0 && (
                          <span
                            className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 border border-blue-200 flex items-center gap-0.5"
                            title={`${estimateCount} estimate${estimateCount !== 1 ? 's' : ''}`}
                            data-testid={`est-count-badge-${lead._id}`}
                          >
                            <FileText className="w-2.5 h-2.5" />
                            {estimateCount}
                          </span>
                        )}
                      </div>
                      {lead.estimated_value > 0 && (
                        <p className="text-sm font-bold text-emerald-600 mt-0.5">{fmt(lead.estimated_value)}</p>
                      )}
                      {lead.description && <p className="text-xs text-gray-500 mt-0.5">{lead.description}</p>}
                      {lead.reason && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          <span className="font-medium text-gray-500">Reason: </span>
                          {reasonCategory && <span className="font-medium text-gray-500">{reasonCategory} · </span>}
                          {lead.reason}
                          {lead.reason_comment ? ` — ${lead.reason_comment}` : ''}
                        </p>
                      )}
                      {lead.follow_up_date && (
                        <p className="text-xs text-amber-600 mt-0.5">
                          Follow-up: {new Date(lead.follow_up_date).toLocaleDateString('en-IN')} ({lead.follow_up_salesman_name || 'N/A'})
                        </p>
                      )}
                      {linkedEsts.length > 0 && (
                        <div className="mt-1 space-y-1">
                          {linkedEsts.map((est, idx) => (
                            <div key={est.id || idx} className="flex items-center gap-1 flex-wrap" data-testid={`est-line-${est.estimate_number}`}>
                              <div
                                className="flex items-center gap-1 cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1 transition-colors flex-1 min-w-0"
                                onClick={e => { e.stopPropagation(); openEstimateDetail(est, lead); }}
                              >
                                <FileText className="w-3 h-3 text-blue-500 flex-shrink-0" />
                                <span className="text-xs text-blue-600 font-medium underline decoration-dotted truncate">
                                  {est.estimate_number} — {fmt(est.total_amount)}
                                </span>
                                <span className={`px-1 py-0.5 rounded text-[9px] font-bold flex-shrink-0 ${est.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                  {est.status === 'confirmed' ? 'Confirmed' : 'Draft'}
                                </span>
                              </div>
                              {canManageLeads && (
                                <button
                                  onClick={e => handleConfirmEstimate(est, e)}
                                  className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-green-400 text-green-700 hover:bg-green-50 flex-shrink-0 transition-colors"
                                  title="Mark as confirmed"
                                  data-testid={`confirm-est-btn-${est.estimate_number}`}
                                >
                                  Confirm
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {isRepairLead && repairEsts.length > 0 && (
                        <div className="mt-1 space-y-1">
                          {repairEsts.filter(re => re.status !== 'superseded').map((re, idx) => (
                            <div key={re.id || idx} className="flex items-center gap-1 flex-wrap" data-testid={`repair-est-line-${re.estimate_number}`}>
                              <div className="flex items-center gap-1 flex-1 min-w-0">
                                <FileText className="w-3 h-3 text-teal-500 flex-shrink-0" />
                                <span className="text-xs text-teal-700 font-medium truncate">
                                  {re.estimate_number} — {re.customer_pays ? 'Customer pays' : 'Refund'} {fmtCurrency(Math.abs(re.net_amount || 0))}
                                </span>
                                <span className={`px-1 py-0.5 rounded text-[9px] font-bold flex-shrink-0 ${re.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                  {re.status === 'active' ? 'Confirmed' : 'Draft'}
                                </span>
                              </div>
                              {canManageLeads && re.status === 'draft' && (
                                <button
                                  onClick={e => handleConfirmRepairEstimate(re, e)}
                                  className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-green-400 text-green-700 hover:bg-green-50 flex-shrink-0 transition-colors"
                                  title="Mark as confirmed"
                                  data-testid={`confirm-repair-est-btn-${re.estimate_number}`}
                                >
                                  Confirm
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                        <div className="mt-1 space-y-1">
                          {exEsts.map((ex, idx) => (
                            <div key={ex.estimate_number || idx} className="flex items-center gap-1" data-testid={`ex-est-line-${ex.estimate_number}`}>
                              <div
                                className="flex items-center gap-1 flex-1 min-w-0 cursor-pointer hover:bg-amber-50 rounded px-1 -mx-1 transition-colors"
                                onClick={e => { e.stopPropagation(); setViewExchangeEst(ex); }}
                              >
                                <span className="text-amber-500 text-[10px]">●</span>
                                <span className="text-xs text-amber-700 font-medium underline decoration-dotted truncate">
                                  {ex.estimate_number} — {fmtCurrency(ex.exchange_value)}
                                </span>
                                <span className="px-1 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-700 flex-shrink-0">
                                  Old Gold
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {lead.linked_order_id && (
                        <button
                          onClick={() => navigate(`/messenger?order=${lead.linked_order_id}`)}
                          className="flex items-center gap-1 mt-1 hover:underline"
                          data-testid={`lead-view-order-btn-${lead._id}`}
                        >
                          <ShoppingCart className="w-3 h-3 text-green-500" />
                          <span className="text-xs text-green-600 font-medium">Order created — View Order</span>
                          <ExternalLink className="w-3 h-3 text-green-500" />
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <Button
                        size="sm" variant="outline"
                        className="h-7 px-2 text-xs text-purple-600 border-purple-200"
                        onClick={() => openNotebook(lead)}
                        data-testid={`notes-btn-${lead._id}`}
                      >
                        <BookOpen className="w-3 h-3 mr-1" /> Notes
                      </Button>
                      {(lead.status === 'open' || lead.status === 'won') && canManageLeads && (
                        <Button
                          size="sm" variant="ghost"
                          className="h-6 px-2 text-[10px] text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed"
                          onClick={() => handleCreateEstimateForLead(lead)}
                          disabled={!session?.client_name || !session?.client_phone}
                          title={!session?.client_name || !session?.client_phone ? 'Add client name & phone first' : ''}
                          data-testid={`add-est-btn-${lead._id}`}
                        >
                          <Plus className="w-3 h-3 mr-1" /> {isRepairLead ? 'Add Repair Estimate' : 'Add Estimate'}
                        </Button>
                      )}
                      {(lead.status === 'open' || lead.status === 'won') && canManageLeads && (
                        <Button
                          size="sm" variant="ghost"
                          className="h-6 px-2 text-[10px] text-amber-600"
                          onClick={() => openOldGoldDialog(lead)}
                          data-testid={`old-gold-btn-${lead._id}`}
                        >
                          Old Gold
                        </Button>
                      )}
                      {lead.status === 'open' && canManageLeads && (
                        <Button
                          size="sm" variant="outline" className="h-7 px-2 text-xs"
                          onClick={() => openStatusDialog(lead)}
                          data-testid={`status-btn-${lead._id}`}
                        >
                          Update Status
                        </Button>
                      )}
                      {lead.status === 'parked' && canManageLeads && (
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 px-2 text-xs text-amber-600"
                          onClick={() => handleReopenLead(lead._id)}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" /> Reopen
                        </Button>
                      )}
                      {lead.status === 'parked' && canManageLeads && (
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => { setRescheduleTarget(lead); setRescheduleDate(lead.follow_up_date || ''); }}
                          data-testid={`reschedule-btn-${lead._id}`}
                        >
                          <Calendar className="w-3 h-3 mr-1" /> Reschedule
                        </Button>
                      )}
                      {lead.status === 'won' && !lead.linked_order_id && hasConfirmedEst && canManageLeads && (
                        <Button
                          size="sm"
                          className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700"
                          onClick={() => handleConvertToOrder(lead)}
                          data-testid={`convert-order-btn-${lead._id}`}
                        >
                          <ShoppingCart className="w-3 h-3 mr-1" /> Convert to Order
                        </Button>
                      )}
                      {lead.status === 'won' && !lead.linked_order_id && estimateCount > 0 && !hasConfirmedEst && (
                        <span className="text-[10px] text-amber-600">Confirm estimate first</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadsTab;
